import React, { useState, useEffect } from "react"
import { Col, Row, Spinner, Table } from "reactstrap"
import api from "API/Api"
import { usePermissions } from "API/PermissionContext"

const PaymentReport = () => {
  const { canExport, MODULE } = usePermissions()
  const MID = MODULE.REPORTS

  const [loading, setLoading] = useState(true)
  const [batches, setBatches] = useState([])
  const [statusBreakdown, setStatusBreakdown] = useState({})
  const [modeBreakdown, setModeBreakdown] = useState({})
  const [trend, setTrend] = useState([])
  const [stats, setStats] = useState(null)

  const [from, setFrom] = useState(new Date(new Date().getFullYear(), new Date().getMonth() - 2, 1).toISOString().slice(0, 10))
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10))

  useEffect(() => { loadData() }, [from, to])

  const loadData = async () => {
    setLoading(true)
    try {
      const resp = await api.get("reports/payment-summary", { params: { from, to } })
      if (resp.data.success) {
        const d = resp.data.data
        setBatches(d.batches || [])
        setStatusBreakdown(d.status_breakdown || {})
        setModeBreakdown(d.mode_breakdown || {})
        setTrend(d.trend || [])
        setStats(d.stats || null)
      }
    } catch (e) { console.error(e) }
    setLoading(false)
  }

  const statusBadge = (s) => {
    const m = { draft: "bg-secondary", pending_approval: "bg-warning", approved: "bg-info", processing: "bg-primary", completed: "bg-success", partial: "bg-warning", failed: "bg-danger" }
    return <span className={`badge ${m[s] || "bg-secondary"}`} style={{ fontSize: "10px" }}>{(s || "").replace(/_/g, " ").toUpperCase()}</span>
  }

  const cardStyle = (color) => ({
    background: `linear-gradient(135deg, ${color}15, ${color}08)`, borderLeft: `4px solid ${color}`,
    borderRadius: "12px", padding: "20px", boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
  })

  const maxTrend = Math.max(...trend.map(t => t.amount), 1)

  return (
    <div className="dashBody">
      <div className="font-size-18 mb-0 dashHeader">
        <Row>
          <Col md={6}><h4><i className="fas fa-money-check-alt me-2" style={{ color: "#6366f1" }}></i>Payment Report</h4></Col>
          <Col md={6} style={{ textAlign: "right" }}>
            <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end", alignItems: "center" }}>
              <input type="date" className="form-control form-control-sm" style={{ width: "140px" }} value={from} onChange={e => setFrom(e.target.value)} />
              <span style={{ color: "#6c757d" }}>to</span>
              <input type="date" className="form-control form-control-sm" style={{ width: "140px" }} value={to} onChange={e => setTo(e.target.value)} />
            </div>
          </Col>
        </Row>
      </div>
      <div className="dashContent">

        {loading ? <div style={{ textAlign: "center", padding: "60px" }}><Spinner color="primary" /></div> : (
          <>
            {/* Stats Cards */}
            {stats && (
              <Row className="mb-4">
                {[
                  { label: "Total Paid", value: "₹" + Number(stats.totalPaid).toLocaleString("en-IN", { minimumFractionDigits: 2 }), color: "#22c55e" },
                  { label: "Pending / Approved", value: "₹" + Number(stats.totalPending).toLocaleString("en-IN", { minimumFractionDigits: 2 }), color: "#f59e0b" },
                  { label: "Held", value: "₹" + Number(stats.totalHeld).toLocaleString("en-IN", { minimumFractionDigits: 2 }), color: "#ef4444" },
                  { label: "Failed", value: "₹" + Number(stats.totalFailed).toLocaleString("en-IN", { minimumFractionDigits: 2 }), color: "#dc2626" },
                  { label: "Total Batches", value: stats.batchCount, color: "#6366f1" },
                ].map((c, i) => (
                  <Col key={i} className="mb-3">
                    <div style={cardStyle(c.color)}>
                      <div style={{ fontSize: "10px", color: "#6c757d", textTransform: "uppercase", letterSpacing: "1px", fontWeight: "600" }}>{c.label}</div>
                      <div style={{ fontSize: "20px", fontWeight: "700", color: "#1e293b", marginTop: "4px" }}>{c.value}</div>
                    </div>
                  </Col>
                ))}
              </Row>
            )}

            {/* Status & Mode Breakdown */}
            <Row className="mb-4">
              <Col md={6}>
                <div style={{ background: "#fff", borderRadius: "12px", padding: "20px", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
                  <h6 style={{ fontWeight: "700", color: "#334155", marginBottom: "12px" }}><i className="fas fa-chart-pie me-2"></i>Status Breakdown</h6>
                  {Object.keys(statusBreakdown).length > 0 ? Object.entries(statusBreakdown).map(([status, data]) => (
                    <div key={status} style={{ display: "flex", justifyContent: "space-between", padding: "8px 12px", borderBottom: "1px solid #f1f5f9", alignItems: "center" }}>
                      <span style={{ textTransform: "capitalize", fontWeight: "500", fontSize: "13px" }}>{status}</span>
                      <div>
                        <span className="badge bg-outline-secondary" style={{ border: "1px solid #94a3b8", color: "#64748b", background: "transparent", marginRight: "8px" }}>{data.count}</span>
                        <span style={{ fontWeight: "700", color: "#059669" }}>₹{Number(data.total || 0).toLocaleString("en-IN")}</span>
                      </div>
                    </div>
                  )) : <p style={{ color: "#94a3b8", textAlign: "center", padding: "20px" }}>No payment data</p>}
                </div>
              </Col>
              <Col md={6}>
                <div style={{ background: "#fff", borderRadius: "12px", padding: "20px", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
                  <h6 style={{ fontWeight: "700", color: "#334155", marginBottom: "12px" }}><i className="fas fa-wallet me-2"></i>Payment Mode Breakdown</h6>
                  {Object.keys(modeBreakdown).length > 0 ? Object.entries(modeBreakdown).map(([mode, data]) => {
                    const icons = { cash: "💵", bank: "🏦", upi: "📱" }
                    return (
                      <div key={mode} style={{ display: "flex", justifyContent: "space-between", padding: "8px 12px", borderBottom: "1px solid #f1f5f9", alignItems: "center" }}>
                        <span style={{ fontWeight: "500", fontSize: "13px" }}>{icons[mode] || ""} {mode?.toUpperCase()}</span>
                        <div>
                          <span className="badge bg-outline-secondary" style={{ border: "1px solid #94a3b8", color: "#64748b", background: "transparent", marginRight: "8px" }}>{data.count}</span>
                          <span style={{ fontWeight: "700", color: "#059669" }}>₹{Number(data.total || 0).toLocaleString("en-IN")}</span>
                        </div>
                      </div>
                    )
                  }) : <p style={{ color: "#94a3b8", textAlign: "center", padding: "20px" }}>No payment data</p>}
                </div>
              </Col>
            </Row>

            {/* Monthly Trend */}
            {trend.length > 0 && (
              <div style={{ background: "#fff", borderRadius: "12px", padding: "20px", marginBottom: "20px", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
                <h6 style={{ fontWeight: "700", color: "#334155", marginBottom: "16px" }}><i className="fas fa-chart-bar me-2"></i>Monthly Payment Trend</h6>
                <div style={{ display: "flex", alignItems: "flex-end", gap: "12px", height: "140px", padding: "0 10px" }}>
                  {trend.map((t, i) => (
                    <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
                      <div style={{ fontSize: "10px", color: "#6c757d", marginBottom: "4px", fontWeight: "600" }}>₹{Number(t.amount || 0).toLocaleString("en-IN")}</div>
                      <div style={{ width: "100%", maxWidth: "60px", height: Math.max(4, (t.amount / maxTrend) * 100) + "px", background: "linear-gradient(180deg, #22c55e, #4ade80)", borderRadius: "6px 6px 0 0", transition: "height 0.5s" }}></div>
                      <div style={{ fontSize: "10px", color: "#6c757d", marginTop: "6px", fontWeight: "500" }}>{t.month}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Batches Table */}
            <div style={{ background: "#fff", borderRadius: "12px", overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
              <div style={{ padding: "16px 20px", borderBottom: "1px solid #e5e7eb" }}>
                <h6 style={{ fontWeight: "700", color: "#334155", margin: 0 }}><i className="fas fa-layer-group me-2"></i>Payment Batches</h6>
              </div>
              {batches.length === 0 ? (
                <div style={{ textAlign: "center", padding: "40px", color: "#94a3b8" }}>No batches in selected period</div>
              ) : (
                <div className="table-responsive">
                  <Table className="table mb-0" size="sm">
                    <thead>
                      <tr style={{ background: "#f8fafc", fontSize: "11px", color: "#64748b", textTransform: "uppercase" }}>
                        <th>Batch</th><th>Period</th><th>Payees</th><th>Gross</th><th>Deductions</th><th>Net</th>
                        <th>Cash</th><th>Bank</th><th>UPI</th><th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {batches.map((b, i) => (
                        <tr key={b.id || i} style={{ fontSize: "12px", borderBottom: "1px solid #f1f5f9" }}>
                          <td style={{ fontWeight: "600", color: "#6366f1" }}>{b.batch_code}</td>
                          <td style={{ whiteSpace: "nowrap", fontSize: "11px" }}>{b.period_from} → {b.period_to}</td>
                          <td style={{ fontWeight: "600" }}>{b.total_payees}</td>
                          <td style={{ color: "#0284c7" }}>₹{Number(b.total_gross || 0).toLocaleString("en-IN")}</td>
                          <td style={{ color: "#dc2626" }}>₹{Number(b.total_deductions || 0).toLocaleString("en-IN")}</td>
                          <td style={{ fontWeight: "700", color: "#059669" }}>₹{Number(b.total_net || 0).toLocaleString("en-IN")}</td>
                          <td style={{ fontSize: "11px" }}>{b.cash_count > 0 ? `${b.cash_count}` : "—"}</td>
                          <td style={{ fontSize: "11px" }}>{b.bank_count > 0 ? `${b.bank_count}` : "—"}</td>
                          <td style={{ fontSize: "11px" }}>{b.upi_count > 0 ? `${b.upi_count}` : "—"}</td>
                          <td>{statusBadge(b.status)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default PaymentReport
